import * as React from "react";
import { CollectionFile } from "../../../../shared/types/files";
import { useSelector } from "react-redux";
import { getRoutinesLoading } from "../../../../selectors/commonSelectors";
import { ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE } from "../../../../constants/ActionTypes";
import { Button } from "../../../../components/ui/button";
import { SplitButton } from "../../../../components/ui/split-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import type { ReduxRootState } from "../../../../types/redux-store";
import {
  CAROUSEL_POST_LIMIT,
  CREATE_POST_LIMIT_REASON,
} from "../../../post/bulk-create/limit-config";

export function SelectedMediaFiles(props: {
  limit: number;
  selectedMediaFiles: CollectionFile[];
  onRemove(file: CollectionFile): void;
  onClear(): void;
  onSubmit(): void;
  onBulkPosts?(): void;
  onBulkStories?(): void;
  onSkip?(): void;
  onCancel?(): void;
  title?: string;
  buttonTitle?: string;
  showBulkActions?: boolean;
  enforceCarouselLimit?: boolean;
  disabled: boolean;
  renderActionButtons?(files: CollectionFile[]): React.ReactNode;
}): JSX.Element {
  const {
    buttonTitle = "Create post",
    showBulkActions = true,
    enforceCarouselLimit = true,
    selectedMediaFiles,
    onClear,
    onSubmit,
    onBulkPosts,
    onBulkStories,
    onCancel,
    onSkip,
    disabled,
  } = props;

  const isUploadingImages = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE])
  );

  const selectedCount = selectedMediaFiles.length;
  const bulkMenuItems = onBulkStories
    ? [
        {
          key: "bulk-stories",
          label: "Bulk Stories",
          onSelect: () => onBulkStories(),
          disabled: isUploadingImages,
        },
      ]
    : [];

  // Button state logic per Phase 5 requirements
  const cancelEnabled = true;
  const bulkPostsEnabled = selectedCount >= 2;
  const bulkDisabled = !bulkPostsEnabled || isUploadingImages;
  const overCarouselLimit =
    enforceCarouselLimit && selectedCount > CAROUSEL_POST_LIMIT;
  // The sentence points at Bulk posts, so only where that button exists.
  const showLimitReason = overCarouselLimit && showBulkActions;
  const limitReasonId = React.useId();
  const primaryActionEnabled = enforceCarouselLimit
    ? // Post mode: enabled for skip (0) and the carousel range
      !overCarouselLimit
    : // Select-only mode: require at least one selection
      selectedCount > 0;

  return (
    <TooltipProvider>
      <div className="w-full">
        <div className="flex items-center justify-between">
          {/* Left side: Cancel button */}
          <Button
            variant="outline"
            size="md"
            onClick={onCancel || onClear}
            disabled={!cancelEnabled}
            className="h-8 border-neutral-100"
          >
            Cancel
          </Button>

          {/* Right side: Skip link, Bulk posts split button, Create post */}
          <div className="flex items-center gap-3">
            {/* Visible, not hover-only: a disabled button explains nothing (SKE-4715) */}
            {showLimitReason && (
              <p
                id={limitReasonId}
                className="min-w-0 max-w-[320px] text-right font-inter text-xs text-neutral-600"
              >
                {CREATE_POST_LIMIT_REASON}
              </p>
            )}

            {onSkip && (
              <Button
                variant="ghost"
                size="md"
                onClick={onSkip}
                className="h-8 text-neutral-700 hover:text-neutral-900"
              >
                Skip
              </Button>
            )}

            {/* Bulk posts split button with tooltip when disabled */}
            {showBulkActions && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SplitButton
                      variant="outline"
                      size="sm"
                      disabled={bulkDisabled}
                      leftLabel="Bulk posts"
                      onLeftClick={() => (onBulkPosts || onSubmit)?.()}
                      menuTriggerAriaLabel="More bulk options"
                      menuItems={bulkMenuItems}
                    />
                  </div>
                </TooltipTrigger>
                {!bulkPostsEnabled && (
                  <TooltipContent>
                    <p>Select 2 or more assets to use bulk create options</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}

            <Button
              variant="blue-primary"
              size="md"
              onClick={selectedCount === 0 && onSkip ? onSkip : onSubmit}
              disabled={!primaryActionEnabled || disabled || isUploadingImages}
              className="h-8"
              aria-describedby={showLimitReason ? limitReasonId : undefined}
            >
              {buttonTitle}
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
